// ─────────────────────────────────────────────────────────────────────────────
// Medical History catalogue — a client-side COPY of server truth.
//
// Source: docs/requirements/MEDICAL_HISTORY_UI_GUIDE.md §5 (103 codes).
//
// There is NO rule converting a code to a label or back. `Painkillers` renders
// as "Pain killers (please specify)", `Anaemia` as "Anaemia / Low Blood Count".
// The labels are hand-written server-side, so this table is transcribed, never
// derived. Never invent a code; never send a label where a code belongs.
//
// Re-sync this file when the API version changes.
// ─────────────────────────────────────────────────────────────────────────────
import { MedicalHistoryCategory } from '../api/generated/models/MedicalHistoryCategory';
import { MedicalHistoryField } from '../api/generated/models/MedicalHistoryField';
import { newGuid } from '../utilis/baseEntity';

export interface CatalogueEntry {
  field: MedicalHistoryField;
  category: MedicalHistoryCategory;
  code: string;
  label: string;
  /** Entry expects free text alongside it (a dose, a number, a year). */
  wantsValue?: boolean;
}

const C = MedicalHistoryCategory;
const F = MedicalHistoryField;

// ─── Medical History field ───────────────────────────────────────────────────

const CONDITIONS: ReadonlyArray<[string, string]> = [
  ['AIDSHIVPositive', 'AIDS/HIV Positive'],
  ['Anaphylaxis', 'Anaphylaxis'],
  ['Anaemia', 'Anaemia / Low Blood Count'],
  ['Angina', 'Angina'],
  ['Anxiety', 'Anxiety'],
  ['Arthritis', 'Arthritis'],
  ['ArtificialHeartValve', 'Artificial Heart Valve'],
  ['Asthma', 'Asthma'],
  ['BloodDisorders', 'Blood Disorders'],
  ['BreathingProblems', 'Breathing Problems'],
  ['BruisingTendency', 'Bruising Tendency'],
  ['CerebralPalsy', 'Cerebral Palsy'],
  ['ChestPains', 'Chest Pains'],
  ['Convulsions', 'Convulsions'],
  ['Depression', 'Depression'],
  ['DiabetesInsulinDependent', 'Diabetes (Insulin Dependent)'],
  ['DiabetesTypeII', 'Diabetes (Type II)'],
  ['DrugAddiction', 'Drug Addiction'],
  ['EmphysemaCOPD', 'Emphysema / COPD'],
  ['EpilepsyOrSeizures', 'Epilepsy or Seizures'],
  ['ExcessiveBleeding', 'Excessive Bleeding'],
  ['ExcessiveThirst', 'Excessive Thirst'],
  ['FaintingOrDizziness', 'Fainting / Dizziness'],
  ['FrequentCough', 'Frequent Cough'],
  ['FrequentDiarrhoea', 'Frequent Diarrhoea'],
  ['FrequentHeadaches', 'Frequent Headaches'],
  ['Glaucoma', 'Glaucoma'],
  ['Gout', 'Gout'],
  ['Haemophilia', 'Haemophilia'],
  ['HayFever', 'Hay Fever'],
  ['HeartAttack', 'Heart Attack'],
  ['HeartFailure', 'Heart Failure'],
  ['HeartMurmur', 'Heart Murmur'],
  ['HeartPacemaker', 'Heart Pacemaker'],
  ['HeavySnoring', 'Heavy Snoring'],
  ['HepatitisBOrC', 'Hepatitis B or C'],
  ['HereditaryDiseases', 'Hereditary Diseases'],
  ['HighBloodPressure', 'High Blood Pressure'],
  ['HighCholesterol', 'High Cholesterol'],
  ['Indigestion', 'Indigestion'],
  ['IrregularHeartbeat', 'Irregular Heartbeat'],
  ['KidneyProblems', 'Kidney Problems'],
  ['LearningDifficulties', 'Learning Difficulties'],
  ['Leukemia', 'Leukemia'],
  ['LiverDisease', 'Liver Disease'],
  ['LowBloodPressure', 'Low Blood Pressure'],
  ['LowBloodSugar', 'Low Blood Sugar'],
  ['LungDisease', 'Lung Disease'],
  ['MuscleWeakness', 'Muscle Weakness'],
  ['Porphyria', 'Porphyria'],
  ['PsychiatricCare', 'Psychiatric Care'],
  ['RecentWeightLoss', 'Recent Weight Loss'],
  ['Reflux', 'Reflux'],
  ['RheumaticFever', 'Rheumatic Fever'],
  ['SickleCellDisorder', 'Sickle Cell Disorder'],
  ['SleepApnoea', 'Sleep Apnoea'],
  ['SteroidMedicine', 'Steroid Medicine'],
  ['StomachUlcers', 'Stomach Ulcers'],
  ['Stroke', 'Stroke'],
  ['SwellingOfLimbs', 'Swelling of Limbs'],
  ['Thalassaemia', 'Thalassaemia'],
  ['ThyroidDisease', 'Thyroid Disease'],
  ['YellowJaundice', 'Yellow Jaundice'],
];

// `value` holds a count or a year for the first four.
const LIFESTYLE: ReadonlyArray<[string, string, boolean]> = [
  ['Smoking', 'I smoking cigarettes, number per day', true],
  ['StoppedSmoking', 'I stopped smoking years ago', true],
  ['StoppedSmokingYear', 'I stopped smoking in year', true],
  ['AlcoholConsumption', 'I consume alcohol, number of units of per week', true],
  ['RecreationalDrugs', 'I have used/use recreational drugs', false],
  ['eCigarettes', 'I use e-cigarettes/vapes', false],
  ['StoppedDrinkingAlcohol', 'I stopped drinking alcohol', false],
];

const GENERAL_HEALTH: ReadonlyArray<[string, string]> = [
  ['SkinRashes', 'I easily get skin rashes'],
];

const WOMENS_HEALTH: ReadonlyArray<[string, string]> = [
  ['Pregnancy', 'I am or could be pregnant'],
  ['OralContraceptives', 'I am taking oral contraceptives'],
  ['Breastfeeding', 'I am currently breastfeeding'],
];

// Questions the patient pack asks. Excluded from the "add" picker (§6) — a
// clinician does not *add* these, they flip them. Excluding them also removes
// most of the duplicate-concept overlap catalogued in §9.
const SCREENING: ReadonlyArray<[string, string]> = [
  ['DiscussInConfidenceWithSedationist', 'I would like to Discuss In Confidence With Sedationist'],
  ['FeelFearAnxietyDistressRequestedSedation', 'I Feel Fear Anxiety Distress Requested Sedation'],
  ['DoctorRecommendedSedation', 'My Doctor Recommended Sedation'],
  ['OtherConditionsNotListed', 'Any other conditions not listed'],
  ['OnGlp1Medication', 'On GLP-1 weight-loss / diabetes medication'],
  ['Vapes', 'Vapes (e-cigarettes)'],
  ['UsingRecreationalDrugs', 'I am presently using recreational drugs'],
  ['EasilyGetSkinRashes', 'I easily get skin rashes'],
  ['HadSedationBefore', "I've had a Sedation before"],
  ['UsesECigarettes', 'I use e-cigarettes'],
  ['StoppedDrinkingAlcohol', 'I have stopped drinking alcohol'],
  ['CouldBePregnant', 'I could be pregnant'],
  ['TakingOralContraceptives', 'I am taking oral contraceptives'],
  ['PresentlyBreastfeeding', 'I am presently breastfeeding'],
];

// ─── Allergies field ─────────────────────────────────────────────────────────

const ALLERGIES: ReadonlyArray<[string, string, boolean]> = [
  ['Penicillin', 'Penicillin', false],
  ['Antibiotics', 'Other Antibiotics (please specify)', true],
  ['Aspirin', 'Aspirin', false],
  ['Painkillers', 'Pain killers (please specify)', true],
  ['Elastoplast', 'Elastoplast', false],
  ['Sedatives', 'Sedatives (please specify)', true],
  ['SleepingPills', 'Sleeping Pills (please specify)', true],
  ['Latex', 'Latex', false],
  ['LocalAnaesthetics', 'Local Anaesthetics', false],
  ['FoodAllergy', 'Food Allergy', false],
  ['CatsDogsDustPollen', 'Cats, dogs, dust or pollen', false],
  ['AnyOtherMedicines', 'Any other medicines (please specify)', true],
];

// ─── Anaesthetic and Surgical History field ──────────────────────────────────

const ANAESTHETIC_HISTORY: ReadonlyArray<[string, string]> = [
  ['Sedation', "I've had Sedation before"],
  ['GeneralAnaesthesia', "I've had General Anaesthetics"],
  ['SpinalOrEpidural', "I've had a Spinal or Epidural Anaesthetic"],
];

// CurrentMedications (`Medication`) and AdditionalNotes (`Note`) have no
// catalogue at all — everything in those boxes is free text (§5d).

const entry = (
  field: MedicalHistoryField,
  category: MedicalHistoryCategory,
  [code, label, wantsValue]: [string, string] | [string, string, boolean]
): CatalogueEntry => ({ field, category, code, label, wantsValue: wantsValue === true });

export const MEDICAL_HISTORY_CATALOGUE: readonly CatalogueEntry[] = [
  ...CONDITIONS.map((r) => entry(F.MEDICAL_HISTORY, C.CONDITION, r)),
  ...LIFESTYLE.map((r) => entry(F.MEDICAL_HISTORY, C.LIFESTYLE, r)),
  ...GENERAL_HEALTH.map((r) => entry(F.MEDICAL_HISTORY, C.GENERAL_HEALTH, r)),
  ...WOMENS_HEALTH.map((r) => entry(F.MEDICAL_HISTORY, C.WOMENS_HEALTH, r)),
  ...SCREENING.map((r) => entry(F.MEDICAL_HISTORY, C.SCREENING, r)),
  ...ALLERGIES.map((r) => entry(F.ALLERGIES, C.ALLERGY, r)),
  ...ANAESTHETIC_HISTORY.map((r) => entry(F.ANAESTHETIC_AND_SURGICAL_HISTORY, C.ANAESTHETIC_HISTORY, r)),
];

/**
 * The category a free-text entry gets when typed into a given box (§6).
 * Note MedicalHistory maps to Condition, NOT Note — `Note` routes to the
 * Additional Notes field, so the entry would vanish from the box it was
 * typed into.
 */
export const DEFAULT_CATEGORY_FOR_FIELD: Record<MedicalHistoryField, MedicalHistoryCategory> = {
  [F.MEDICAL_HISTORY]: C.CONDITION,
  [F.CURRENT_MEDICATIONS]: C.MEDICATION,
  [F.ALLERGIES]: C.ALLERGY,
  [F.ANAESTHETIC_AND_SURGICAL_HISTORY]: C.ANAESTHETIC_HISTORY,
  [F.ADDITIONAL_NOTES]: C.NOTE,
};

export const FIELD_HEADINGS: Record<MedicalHistoryField, string> = {
  [F.MEDICAL_HISTORY]: 'Medical History',
  [F.CURRENT_MEDICATIONS]: 'Current Medications',
  [F.ALLERGIES]: 'Allergies',
  [F.ANAESTHETIC_AND_SURGICAL_HISTORY]: 'Anaesthetic and Surgical History',
  [F.ADDITIONAL_NOTES]: 'Additional Notes',
};

/**
 * Placeholder for each box's add field. It has to carry the whole instruction,
 * because it is the only wording a clinician reads before typing: the boxes
 * with a catalogue say suggestions exist AND that your own words are fine; the
 * two without one (§5d) just say what to type.
 */
export const ADD_PLACEHOLDER: Record<MedicalHistoryField, string> = {
  [F.MEDICAL_HISTORY]: 'Type a condition — pick a suggestion or use your own words',
  [F.CURRENT_MEDICATIONS]: 'Type a medication, then press Add',
  [F.ALLERGIES]: 'Type an allergy — pick a suggestion or use your own words',
  [F.ANAESTHETIC_AND_SURGICAL_HISTORY]: 'Type a procedure — pick a suggestion or use your own words',
  [F.ADDITIONAL_NOTES]: 'Type a note, then press Add',
};

export const FIELD_ORDER: readonly MedicalHistoryField[] = [
  F.MEDICAL_HISTORY,
  F.CURRENT_MEDICATIONS,
  F.ALLERGIES,
  F.ANAESTHETIC_AND_SURGICAL_HISTORY,
  F.ADDITIONAL_NOTES,
];

const CLINICIAN_NOTE_PREFIX = 'ClinicianNote:';

/**
 * Codes the patient pack owns — exactly one row per patient per category, so
 * writing to them destroys what the patient wrote (§5e). Never send these.
 */
const RESERVED_CODES = new Set(['AdditionalNotes', 'CurrentMedications']);

const byCategoryAndCode = new Map<string, CatalogueEntry>(
  MEDICAL_HISTORY_CATALOGUE.map((e) => [`${e.category}|${e.code}`, e])
);

/** Catalogue options offered in the picker for a box. Screening is excluded (§6). */
export function catalogueForField(field: MedicalHistoryField): CatalogueEntry[] {
  return MEDICAL_HISTORY_CATALOGUE.filter(
    (e) => e.field === field && e.category !== C.SCREENING
  );
}

/** The Screening questions, rendered as toggles rather than picker options (§6). */
export function screeningEntries(): CatalogueEntry[] {
  return MEDICAL_HISTORY_CATALOGUE.filter((e) => e.category === C.SCREENING);
}

export function lookupEntry(
  category: MedicalHistoryCategory | undefined,
  code: string | null | undefined
): CatalogueEntry | undefined {
  if (!category || !code) return undefined;
  return byCategoryAndCode.get(`${category}|${code}`);
}

export function isClinicianNote(code: string | null | undefined): boolean {
  return typeof code === 'string' && code.startsWith(CLINICIAN_NOTE_PREFIX);
}

export function isReservedCode(code: string | null | undefined): boolean {
  return typeof code === 'string' && RESERVED_CODES.has(code);
}

/**
 * Mint a stable free-text code. Generated ONCE when the clinician creates the
 * entry and reused on every subsequent sync — the server mints a fresh GUID for
 * any entry sent with a null code, so a retry would duplicate the note (§4).
 */
export function newClinicianNoteCode(): string {
  return `${CLINICIAN_NOTE_PREFIX}${newGuid().replace(/-/g, '')}`;
}

/**
 * What to show for an item. The server builds `label` as "{label}: {value}"
 * when the two differ, so rendering label AND value shows the value twice (§8).
 * For a ClinicianNote the label IS the typed text.
 * Falls back to the server label, then the raw code — a retired enum member
 * must still render rather than crash (§8).
 */
export function displayLabel(item: {
  Category?: MedicalHistoryCategory;
  Code?: string | null;
  Label?: string | null;
  Value?: string | null;
}): string {
  if (isClinicianNote(item.Code)) return item.Value || item.Label || '';
  if (item.Label) return item.Label;
  const found = lookupEntry(item.Category, item.Code);
  if (found) return item.Value ? `${found.label}: ${item.Value}` : found.label;
  return item.Code || '';
}
